import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

/**
 * Handles the return from https://auth.emergentagent.com — parses the URL fragment
 * `#session_id=...`, exchanges it via the backend, then navigates to the dashboard
 * (or /admin/orders for admins). Uses `useRef` for a synchronous processed-flag to
 * avoid double-exchange under React StrictMode.
 */
export default function AuthCallback() {
  const { t } = useTranslation();
  const { googleExchange } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    const refCode = sessionStorage.getItem("jl_pending_ref") || undefined;

    (async () => {
      try {
        const user = await googleExchange(sessionId, refCode);
        sessionStorage.removeItem("jl_pending_ref");
        // Clean the URL fragment so a hard-refresh doesn't retry.
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(t("auth.welcome"));
        if (user?.role === "admin") navigate("/admin/orders", { replace: true });
        else navigate("/dashboard", { replace: true });
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/login", { replace: true });
      }
    })();
  }, [googleExchange, navigate, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7]" data-testid="auth-callback">
      <div className="text-center">
        <div className="mb-8 flex justify-center"><Logo size="md" /></div>
        <Loader2 size={28} className="animate-spin mx-auto text-[#005870]" />
        <div className="mt-4 text-sm text-[#4a6e77]">{t("auth.signingIn")}</div>
      </div>
    </div>
  );
}
